
import {
  computed,
  defineComponent, onMounted, ref, Ref, watch,
} from 'vue';
import AppHeader from '@/components/AppHeader.vue';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import ItemFile from '@/modules/drive/components/items/ItemFile.vue';
import WebsocketService from '@/services/WebsocketService';
import AppSearch from '@/components/UI/AppSearch.vue';
import AppSkeletonMedia from './skeletons/AppSkeletonMedia.vue';
import AppMessageShareSettings from '@/components/AppMessageShareSettings.vue';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import { SHARE_SETTINGS } from '@/types/enums';

export default defineComponent({
  components: {
    AppHeader,
    AppSearch,
    ItemFile,
    AppSkeletonMedia,
    AppMessageShareSettings,
  },
  emits: ['cancel', 'done'],
  props: {
    isGroup: { type: Boolean, default: false },
  },

  setup(props, { emit }) {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const dataLoading: Ref<boolean> = ref(true);
    const messagesStore = useMessagesStore();

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage,
    } = UsePagination();

    const fileCheck = ref<any>({});
    const selectedList = computed(() => Object.keys(fileCheck.value).filter((item) => fileCheck.value[item]));

    const shareSettings: Ref<SHARE_SETTINGS[]> = ref([]);

    function selectFile(id: string) {
      fileCheck.value = { ...fileCheck.value, [id]: !fileCheck.value[id] };
    }

    function doneAction() {
      if (!selectedList.value.length) {
        return;
      }
      selectedList.value.forEach((id) => {
        WebsocketService.sendMedia(messagesStore!.currentConversation!.info.id, id, props.isGroup, shareSettings.value);
      });
      emit('done');
    }

    async function fetchData() {
      return driveStore.fetchFiles(pageIdx.value);
    }

    function handleScroll() {
      onScroll(fetchData, DIRECTION.BOTTOM, dataLoading.value);
    }

    function updateShareSettings(event) {
      shareSettings.value = [
        event.allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        event.allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        event.allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        pageIdx.value = 0;
        fetchData().then((response) => {
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        });
      }, 300);
    });

    const responseMessage = ref('');
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      if (newSearch.length === 0 || oldSearch.length === 0) {
        responseMessage.value = '';
      }
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        pageIdx.value = 0;
        fetchData().then((response) => {
          if (response.results.data.items.length === 0 && newSearch.length) {
            responseMessage.value = 'No search results found';
          }
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        });
      }, 300);
    });

    onMounted(() => {
      driveStore.search = '';
      shareSettings.value = [
        mainStore.sharingSettings[0].allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        mainStore.sharingSettings[0].allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        mainStore.sharingSettings[0].allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
      fetchData()
        .then((response) => {
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        })
        .finally(() => {
          dataLoading.value = false;
        });
    });

    return {
      scroller,
      handleScroll,
      driveStore,
      dataLoading,
      fetchData,
      selectedList,
      responseMessage,
      doneAction,
      selectFile,
      updateShareSettings,
      fileCheck,
      isPageLoading,
      shareSettings,
    };
  },
});
